import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { RefreshCw, ArrowUpRight, Plus, X, Trash2, Clock } from 'lucide-react'
import { ArmedProvider, useArmed, confirmDestructive } from '../components/destructive'
import {
  fetchStepOutdatedCounts,
  markResultsStale,
  me,
  promotePromptVersion,
  promptSlots as slotsApi,
  promptVersions,
  results as resultsApi,
  runStale,
  stepDeps as stepDepsApi,
  steps as stepsApi,
  type ComputationStep,
  type PromptSlot,
  type PromptVersion,
  type StepStaleCount,
} from '../api'

const NODE_W = 196
const NODE_H = 64
const X_GAP = 88
const Y_GAP = 36
const PADDING = 36

type StepState = 'idle' | 'fresh' | 'stale' | 'mixed'

export default function PipelinePage() {
  return (
    <ArmedProvider scope="pipeline">
      <PipelinePageInner />
    </ArmedProvider>
  )
}

function PipelinePageInner() {
  const qc = useQueryClient()
  const steps = useQuery({ queryKey: ['steps'], queryFn: () => stepsApi.list() })
  // (Was a paginated /api/results/ fetch used for header counts + per-step
  // state; replaced by the dedicated outdated-counts endpoint above.)
  // Total run count — fetch only the count from the paginated envelope
  // (DRF returns {count, results} so a 1-row request gives us the total
  // without dragging back the full result list).
  const runsCount = useQuery({
    queryKey: ['runs-count'],
    queryFn: async () => {
      const r = await fetch('/api/runs/?limit=1', { credentials: 'include' })
      const data = await r.json()
      return (data?.count as number) ?? 0
    },
  })
  // Look up the current user's tenant once so outdated-count + mark-stale
  // calls can scope to the right client without an extra picker UI.
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => me() })
  const clientCode = meQuery.data?.client?.code
  const outdatedCounts = useQuery({
    queryKey: ['step-outdated-counts', clientCode],
    queryFn: () => fetchStepOutdatedCounts(clientCode),
    enabled: !!clientCode,
  })
  const outdatedByStepId = useMemo(() => {
    const out: Record<number, StepStaleCount> = {}
    for (const s of outdatedCounts.data?.steps ?? []) out[s.step_id] = s
    return out
  }, [outdatedCounts.data])
  const [selectedId, setSelectedId] = useState<number | null>(null)

  const runStaleMu = useMutation({
    mutationFn: () => runStale(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['step-outdated-counts'] })
      qc.invalidateQueries({ queryKey: ['runs-count'] })
      qc.invalidateQueries({ queryKey: ['runs'] })
    },
  })

  // Per-step state. Source of truth is the dedicated outdated-counts
  // endpoint (server-aggregated counts of total / stale / outdated for
  // BOTH ComputationResult and MatterStepResult per step). Earlier
  // versions of this page derived totals from a paginated /api/results/
  // fetch — that capped at 50 rows so step counts shown in the UI did
  // NOT reflect CLI / celery / overnight runs that produced thousands.
  const stateByStep = useMemo<Record<number, { state: StepState; total: number; stale: number; failed: number }>>(() => {
    const out: Record<number, { state: StepState; total: number; stale: number; failed: number }> = {}
    for (const s of steps.data || []) out[s.id] = { state: 'idle', total: 0, stale: 0, failed: 0 }
    for (const od of outdatedCounts.data?.steps ?? []) {
      const e = out[od.step_id]
      if (!e) continue
      e.total = od.computation_result.total + od.matter_step_result.total
      e.stale = od.computation_result.stale + od.matter_step_result.stale
    }
    for (const id in out) {
      const e = out[id]
      if (e.total === 0) e.state = 'idle'
      else if (e.stale > 0 && e.stale < e.total) e.state = 'mixed'
      else if (e.stale > 0) e.state = 'stale'
      else e.state = 'fresh'
    }
    return out
  }, [steps.data, outdatedCounts.data])

  // Aggregate "results" badge in the page header — sum of all per-step
  // totals from the same authoritative source.
  const totalResults = useMemo(() => {
    const cs = outdatedCounts.data?.steps ?? []
    return cs.reduce(
      (a, s) => a + s.computation_result.total + s.matter_step_result.total, 0,
    )
  }, [outdatedCounts.data])

  const layout = useMemo(() => computeLayout(steps.data ?? []), [steps.data])
  const selected = (steps.data ?? []).find((s) => s.id === selectedId) ?? null
  const ancestors = selected ? collectAncestors(selected.id, steps.data ?? []) : new Set<number>()
  const descendants = selected ? collectDescendants(selected.id, steps.data ?? []) : new Set<number>()

  const totalStale = Object.values(stateByStep).reduce((a, e) => a + e.stale, 0)
  const { AdminArmingToggle } = useArmed()

  return (
    <div className="grid h-full grid-cols-[1fr_420px] gap-px bg-[color:var(--me-border)]">
      <section className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-6">
        <div className="mb-3 flex items-center gap-3 text-[12px] me-text-dim">
          <span className="me-mono">{steps.data?.length ?? 0} steps</span>
          <span className="me-text-mute">·</span>
          <span className="me-mono">{(runsCount.data ?? 0).toLocaleString()} runs</span>
          <span className="me-text-mute">·</span>
          <span className="me-mono">{totalResults.toLocaleString()} results</span>
          {totalStale > 0 && (
            <>
              <span className="me-text-mute">·</span>
              <span className="me-chip me-chip-stale">{totalStale} stale</span>
              <button
                onClick={() => runStaleMu.mutate()}
                disabled={runStaleMu.isPending}
                className="me-btn me-btn-outline"
              >
                <RefreshCw size={12} />
                {runStaleMu.isPending ? 'Enqueuing…' : 'Run stale'}
              </button>
            </>
          )}
          {runStaleMu.data && (
            <span className="me-chip me-chip-fresh">enqueued {runStaleMu.data.enqueued}</span>
          )}
          <span className="ml-auto" />
          <AdminArmingToggle />
        </div>

        <Legend />

        <div className="me-card mt-3 overflow-auto p-3">
          <svg width={layout.width + PADDING * 2} height={layout.height + PADDING * 2} className="block">
            <g transform={`translate(${PADDING},${PADDING})`}>
              {Array.from({ length: layout.levels }, (_, i) => (
                <text
                  key={i}
                  x={i * (NODE_W + X_GAP) + NODE_W / 2}
                  y={-12}
                  textAnchor="middle"
                  className="fill-[color:var(--me-textMute)] text-[10px] uppercase"
                  style={{ letterSpacing: '0.08em' }}
                >
                  Level {i}
                </text>
              ))}
              {layout.edges.map((e, i) => {
                const isHighlight =
                  selected &&
                  ((selected.id === e.parentId && descendants.has(e.childId)) ||
                    (selected.id === e.childId && ancestors.has(e.parentId)))
                return (
                  <path
                    key={i}
                    d={e.path}
                    fill="none"
                    stroke={isHighlight ? 'var(--me-fg)' : 'var(--me-borderStrong)'}
                    strokeWidth={isHighlight ? 1.6 : 1}
                  />
                )
              })}
              {layout.nodes.map((n) => {
                const stateInfo = stateByStep[n.step.id] || { state: 'idle' as StepState, total: 0, stale: 0, failed: 0 }
                const isSelected = n.step.id === selectedId
                const isRelated = ancestors.has(n.step.id) || descendants.has(n.step.id)
                const fill =
                  isSelected ? 'var(--me-fg)' :
                  stateInfo.state === 'stale' ? 'var(--me-staleBg)' :
                  stateInfo.state === 'fresh' ? 'var(--me-freshBg)' :
                  stateInfo.state === 'mixed' ? 'var(--me-staleBg)' :
                  'var(--me-surface)'
                const stroke =
                  isSelected ? 'var(--me-fg)' :
                  stateInfo.state === 'stale' ? 'var(--me-stale)' :
                  stateInfo.state === 'fresh' ? 'var(--me-fresh)' :
                  stateInfo.state === 'mixed' ? 'var(--me-stale)' :
                  isRelated ? 'var(--me-fg)' : 'var(--me-borderStrong)'
                const textColor = isSelected ? 'var(--me-surface)' : 'var(--me-text)'
                const subColor = isSelected ? 'var(--me-surface)' :
                  stateInfo.state === 'stale' ? 'var(--me-stale)' :
                  stateInfo.state === 'fresh' ? 'var(--me-fresh)' :
                  'var(--me-textMute)'
                return (
                  <g
                    key={n.step.id}
                    transform={`translate(${n.x},${n.y})`}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(n.step.id)}
                  >
                    <rect
                      width={NODE_W}
                      height={NODE_H}
                      rx={8}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={isSelected ? 1.8 : 1.2}
                    />
                    <text
                      x={NODE_W / 2}
                      y={NODE_H / 2 - 4}
                      textAnchor="middle"
                      className="text-[12.5px] font-medium"
                      style={{ fill: textColor }}
                    >
                      {n.step.name}
                    </text>
                    <text
                      x={NODE_W / 2}
                      y={NODE_H / 2 + 14}
                      textAnchor="middle"
                      className="text-[10px]"
                      style={{ fill: subColor, opacity: isSelected ? 0.78 : 1 }}
                    >
                      {(() => {
                        // Total combines per-artifact + per-(artifact,matter) result rows.
                        // Outdated comes from the new endpoint (derived from prompt-version FKs).
                        const od = outdatedByStepId[n.step.id]
                        const totalOutdated = od
                          ? od.computation_result.outdated + od.matter_step_result.outdated
                          : 0
                        if (stateInfo.total === 0 && (!od || (od.computation_result.total + od.matter_step_result.total === 0))) {
                          return `${n.step.slot_name} · no runs yet`
                        }
                        const parts = [`results: ${stateInfo.total}`]
                        if (stateInfo.stale > 0) parts.push(`stale: ${stateInfo.stale}`)
                        if (totalOutdated > 0) parts.push(`outdated: ${totalOutdated}`)
                        return parts.join(' · ')
                      })()}
                    </text>
                  </g>
                )
              })}
            </g>
          </svg>
        </div>
      </section>

      <aside className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-5">
        {selected ? (
          <StepDetail
            step={selected}
            steps={steps.data ?? []}
            stateInfo={stateByStep[selected.id]}
            outdated={outdatedByStepId[selected.id]}
            clientCode={clientCode}
            onChange={() => {
              qc.invalidateQueries({ queryKey: ['step-outdated-counts'] })
      qc.invalidateQueries({ queryKey: ['runs-count'] })
              qc.invalidateQueries({ queryKey: ['steps'] })
              qc.invalidateQueries({ queryKey: ['step-outdated-counts'] })
            }}
          />
        ) : (
          <div className="text-[12px] me-text-mute">Click a step to inspect it.</div>
        )}
      </aside>
    </div>
  )
}

function Legend() {
  const items: { label: string; bg: string; stroke: string }[] = [
    { label: 'fresh',     bg: 'var(--me-freshBg)', stroke: 'var(--me-fresh)' },
    { label: 'mixed',     bg: 'var(--me-staleBg)', stroke: 'var(--me-stale)' },
    { label: 'all stale', bg: 'var(--me-staleBg)', stroke: 'var(--me-stale)' },
    { label: 'idle',      bg: 'var(--me-surface)', stroke: 'var(--me-borderStrong)' },
  ]
  return (
    <div className="flex items-center gap-2 text-[11px] me-text-mute">
      <span className="me-mono text-[10px] uppercase tracking-wider">legend:</span>
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1">
          <span
            className="inline-block h-3 w-3 rounded-sm border"
            style={{ background: it.bg, borderColor: it.stroke }}
          />
          {it.label}
        </span>
      ))}
    </div>
  )
}

function StepDetail({
  step,
  steps,
  stateInfo,
  outdated,
  clientCode,
  onChange,
}: {
  step: ComputationStep
  steps: ComputationStep[]
  stateInfo?: { state: StepState; total: number; stale: number; failed: number }
  outdated?: StepStaleCount
  clientCode?: string
  onChange: () => void
}) {
  const versions = useQuery({
    queryKey: ['versions', 'slot', step.slot],
    queryFn: () => promptVersions.bySlot(step.slot),
  })
  const recent = useQuery({
    queryKey: ['results', 'step', step.id],
    queryFn: () => resultsApi.list({ step: step.id }),
  })

  const productionVersion = useMemo(
    () => (versions.data || []).find((v) => v.labels.includes(step.prompt_version_label)),
    [versions.data, step.prompt_version_label],
  )
  const otherVersions = useMemo(
    () => (versions.data || []).filter((v) => v.id !== productionVersion?.id),
    [versions.data, productionVersion],
  )

  const promote = useMutation({
    mutationFn: (versionId: number) => promotePromptVersion(versionId, step.prompt_version_label),
    onSuccess: () => {
      versions.refetch()
      recent.refetch()
      onChange()
    },
  })


  return (
    <div>
      <div className="me-section-h">Step</div>
      <h3 className="text-[15px] font-medium me-text">{step.name}</h3>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span className="me-chip">L{step.level}</span>
        <span className="me-chip">{step.slot_name}</span>
        <span className="me-chip me-chip-fresh">{step.prompt_version_label}</span>
        <span className="me-chip">{step.backend}</span>
        {stateInfo && stateInfo.total > 0 && (
          <span className={`me-chip ${stateInfo.state === 'fresh' ? 'me-chip-fresh' : stateInfo.state === 'stale' ? 'me-chip-stale' : ''}`}>
            {stateInfo.total} results · {stateInfo.stale} stale
          </span>
        )}
      </div>

      {productionVersion && (
        <div className="mt-5">
          <div className="me-section-h">Production prompt · v{productionVersion.version}</div>
          <pre className="me-mono whitespace-pre-wrap rounded-md bg-[color:var(--me-panel)] p-3 text-[11.5px] leading-relaxed me-text max-h-48 overflow-auto">
            {productionVersion.content}
          </pre>
          {productionVersion.commit_message && (
            <div className="mt-1 text-[11px] me-text-mute italic">{productionVersion.commit_message}</div>
          )}
        </div>
      )}

      <ArmedDagEditor step={step} steps={steps} onChange={onChange} />

      <OutdatedPanel step={step} outdated={outdated} clientCode={clientCode} onChange={onChange} />

      {otherVersions.length > 0 && (
        <div className="mt-5">
          <div className="me-section-h">Other versions</div>
          <ul className="space-y-1">
            {otherVersions.slice(0, 5).map((v) => (
              <PromoteRow key={v.id} v={v} step={step} promote={promote} />
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5">
        <div className="me-section-h">Recent results</div>
        {recent.isLoading && <div className="text-[12px] me-text-mute">loading…</div>}
        <ul className="space-y-1">
          {(recent.data ?? []).slice(0, 6).map((r) => (
            <li key={r.id} className="me-card p-2.5">
              <div className="flex items-center justify-between">
                <span className="me-mono text-[11.5px]">artifact #{r.artifact} · v{r.prompt_version_number}</span>
                <span className={r.is_stale ? 'me-chip me-chip-stale' : 'me-chip me-chip-fresh'}>
                  {r.is_stale ? 'stale' : r.status}
                </span>
              </div>
              <div className="me-mono mt-0.5 text-[10px] me-text-mute">
                {new Date(r.computed_at).toLocaleString()} · {r.latency_ms}ms
              </div>
            </li>
          ))}
          {(recent.data ?? []).length === 0 && <li className="text-[12px] me-text-mute">No results yet.</li>}
        </ul>
      </div>
    </div>
  )
}

// Surfaces the deliberate-stale flow for a single step. Reads outdated
// counts from /api/steps/outdated-counts/, lets the operator add filters
// (date range, matter, artifact ids), previews the count via dry-run,
// and confirms via /api/results/mark-stale/. See
// docs/specs/outdated-vs-stale.md for the why.
function OutdatedPanel({
  step,
  outdated,
  clientCode,
  onChange,
}: {
  step: ComputationStep
  outdated?: StepStaleCount
  clientCode?: string
  onChange: () => void
}) {
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [matterIdsRaw, setMatterIdsRaw] = useState('')
  const [artifactIdsRaw, setArtifactIdsRaw] = useState('')
  const [outdatedOnly, setOutdatedOnly] = useState(true)
  const [preview, setPreview] = useState<{ to_flip: number; total: number } | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)

  const cr = outdated?.computation_result
  const msr = outdated?.matter_step_result
  const totalOutdated = (cr?.outdated ?? 0) + (msr?.outdated ?? 0)
  const totalStale = (cr?.stale ?? 0) + (msr?.stale ?? 0)
  const totalRows = (cr?.total ?? 0) + (msr?.total ?? 0)

  const buildReq = (dryRun: boolean) => {
    const matter_ids = matterIdsRaw.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n))
    const artifact_ids = artifactIdsRaw.split(',').map((s) => s.trim()).filter(Boolean).map(Number).filter((n) => !Number.isNaN(n))
    return {
      client_code: clientCode || '',
      step_names: [step.name],
      matter_ids: matter_ids.length ? matter_ids : undefined,
      artifact_ids: artifact_ids.length ? artifact_ids : undefined,
      source_date_from: dateFrom || undefined,
      source_date_to: dateTo || undefined,
      outdated_only: outdatedOnly,
      dry_run: dryRun,
    }
  }

  const previewMu = useMutation({
    mutationFn: () => markResultsStale(buildReq(true)),
    onSuccess: (res) => {
      setPreview({
        to_flip: res.to_flip.computation_result + res.to_flip.matter_step_result,
        total: res.counts.computation_result + res.counts.matter_step_result,
      })
      setFeedback(null)
    },
    onError: (e: any) => setFeedback(`preview failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`),
  })
  const confirmMu = useMutation({
    mutationFn: () => markResultsStale(buildReq(false)),
    onSuccess: (res) => {
      const flipped = (res.flipped?.computation_result ?? 0) + (res.flipped?.matter_step_result ?? 0)
      setFeedback(`Marked ${flipped} row${flipped === 1 ? '' : 's'} stale.`)
      setPreview(null)
      onChange()
    },
    onError: (e: any) => setFeedback(`failed: ${e?.response?.data?.detail || e?.message || 'unknown'}`),
  })

  // Hide the panel entirely if there's nothing to act on AND no rows for
  // this step in the tenant — keeps the sidebar clean for unused steps.
  if (totalRows === 0 && totalOutdated === 0 && totalStale === 0) return null

  return (
    <div className="mt-5">
      <div className="me-section-h flex items-center gap-2">
        <Clock size={11} /> Mark for rerun
      </div>
      <div className="me-card p-2.5 space-y-2">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="me-mono">{totalRows}</span>
          <span className="me-text-mute">total</span>
          {totalStale > 0 && (
            <>
              <span className="me-text-mute">·</span>
              <span className="me-chip me-chip-stale">{totalStale} already queued</span>
            </>
          )}
          {totalOutdated > 0 && outdated?.production_version != null && (
            <>
              <span className="me-text-mute">·</span>
              <span className="me-chip" style={{ background: 'var(--me-staleBg)', color: 'var(--me-stale)' }}>
                outdated: {totalOutdated} (current prompt is v{outdated.production_version})
              </span>
            </>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px]">
          <label className="space-y-0.5">
            <span className="me-text-mute">Source from</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="me-input me-mono w-full"
            />
          </label>
          <label className="space-y-0.5">
            <span className="me-text-mute">Source to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="me-input me-mono w-full"
            />
          </label>
          <label className="col-span-2 space-y-0.5">
            <span className="me-text-mute">Matter ids (comma)</span>
            <input
              value={matterIdsRaw}
              onChange={(e) => setMatterIdsRaw(e.target.value)}
              placeholder="14, 50"
              className="me-input me-mono w-full"
            />
          </label>
          <label className="col-span-2 space-y-0.5">
            <span className="me-text-mute">Artifact ids (comma)</span>
            <input
              value={artifactIdsRaw}
              onChange={(e) => setArtifactIdsRaw(e.target.value)}
              placeholder="62289, 62240"
              className="me-input me-mono w-full"
            />
          </label>
          <label className="col-span-2 inline-flex items-center gap-2 text-[11px]">
            <input
              type="checkbox"
              checked={outdatedOnly}
              onChange={(e) => { setOutdatedOnly(e.target.checked); setPreview(null) }}
            />
            Outdated only (skip rows already on current production version)
          </label>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => previewMu.mutate()}
            disabled={previewMu.isPending || !clientCode}
            className="me-btn me-btn-outline"
            title="Dry-run; shows count without flipping flags"
          >
            {previewMu.isPending ? 'Counting…' : 'Preview'}
          </button>
          <button
            onClick={() => confirmMu.mutate()}
            disabled={confirmMu.isPending || !clientCode || !preview || preview.to_flip === 0}
            className="me-btn"
            title="Flip is_stale=True on the previewed rows"
          >
            {confirmMu.isPending ? 'Marking…' : `Mark ${preview?.to_flip ?? 0} stale`}
          </button>
        </div>
        {preview && (
          <div className="text-[11px] me-text-mute">
            Preview: {preview.to_flip} of {preview.total} would flip.
          </div>
        )}
        {feedback && (
          <div className="text-[11px]" style={{ color: 'var(--me-fresh)' }}>{feedback}</div>
        )}
      </div>
    </div>
  )
}

function PromoteRow({ v, step, promote }: { v: PromptVersion; step: ComputationStep; promote: ReturnType<typeof useMutation<{ outdated_count?: number; stale_count?: number }, Error, number, unknown>> }) {
  const { armed } = useArmed()
  return (
    <li className="me-card p-2.5">
      <div className="flex items-center justify-between">
        <div>
          <div className="me-mono text-[12px]">v{v.version}</div>
          <div className="text-[11px] me-text-mute italic truncate max-w-xs">
            {v.commit_message || new Date(v.created_at).toLocaleString()}
          </div>
        </div>
        {armed ? (
          <button
            onClick={() => {
              if (confirmDestructive(
                `Promote v${v.version} to "${step.prompt_version_label}" on ${step.name}?\n\n` +
                `Existing results stay attached to their old version (visible as 'outdated' on this page). ` +
                `They aren't auto-marked stale — use 'Mark outdated as stale' to queue them for rerun.`
              )) promote.mutate(v.id)
            }}
            disabled={promote.isPending}
            className="me-btn me-btn-outline"
            title={`Promote v${v.version} to ${step.prompt_version_label}`}
          >
            <ArrowUpRight size={12} />
            Promote
          </button>
        ) : null}
      </div>
    </li>
  )
}

function computeLayout(steps: ComputationStep[]) {
  if (steps.length === 0) return { width: 0, height: 0, levels: 0, nodes: [], edges: [] }
  // Use the slot's level directly (no -1 offset), so L0 (vision pre-pass)
  // sits in column 0 instead of off-canvas at -1. Header below also shows
  // the actual level number.
  const byLevel = new Map<number, ComputationStep[]>()
  for (const s of steps) {
    const lvl = s.level ?? 0
    const arr = byLevel.get(lvl) ?? []
    arr.push(s)
    byLevel.set(lvl, arr)
  }
  const levels = Math.max(...byLevel.keys()) + 1
  const positioned = new Map<number, { x: number; y: number }>()
  const nodes: { step: ComputationStep; x: number; y: number }[] = []
  for (let lvl = 0; lvl < levels; lvl++) {
    const arr = byLevel.get(lvl) ?? []
    arr.forEach((s, idx) => {
      const x = lvl * (NODE_W + X_GAP)
      const y = idx * (NODE_H + Y_GAP)
      positioned.set(s.id, { x, y })
      nodes.push({ step: s, x, y })
    })
  }
  const edges: { parentId: number; childId: number; path: string }[] = []
  for (const s of steps) {
    for (const cid of s.child_ids) {
      const p = positioned.get(s.id)
      const c = positioned.get(cid)
      if (!p || !c) continue
      const x1 = p.x + NODE_W
      const y1 = p.y + NODE_H / 2
      const x2 = c.x
      const y2 = c.y + NODE_H / 2
      const mx = (x1 + x2) / 2
      edges.push({ parentId: s.id, childId: cid, path: `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}` })
    }
  }
  const width = levels * NODE_W + (levels - 1) * X_GAP
  const maxRows = Math.max(...Array.from(byLevel.values(), (a) => a.length))
  const height = maxRows * NODE_H + (maxRows - 1) * Y_GAP
  return { width, height, levels, nodes, edges }
}

function collectAncestors(id: number, steps: ComputationStep[]): Set<number> {
  const byId = new Map(steps.map((s) => [s.id, s]))
  const out = new Set<number>()
  const stack: number[] = [id]
  while (stack.length) {
    const cur = stack.pop()!
    const s = byId.get(cur)
    if (!s) continue
    for (const pid of s.parent_ids) if (!out.has(pid)) { out.add(pid); stack.push(pid) }
  }
  return out
}

function collectDescendants(id: number, steps: ComputationStep[]): Set<number> {
  const byId = new Map(steps.map((s) => [s.id, s]))
  const out = new Set<number>()
  const stack: number[] = [id]
  while (stack.length) {
    const cur = stack.pop()!
    const s = byId.get(cur)
    if (!s) continue
    for (const cid of s.child_ids) if (!out.has(cid)) { out.add(cid); stack.push(cid) }
  }
  return out
}

// ---------- DAG editor (in step-detail pane) ----------

function ArmedDagEditor(props: { step: ComputationStep; steps: ComputationStep[]; onChange: () => void }) {
  // Gate the entire DAG editor on the page's arming switch. When not
  // armed, show a one-line note explaining where the controls went.
  // When armed, show the full editor; the underlying delete still has
  // its own 2-step inline confirm.
  const { armed, isSuperuser } = useArmed()
  if (!isSuperuser) return null
  if (!armed) {
    return (
      <div className="mt-5 me-card p-3 text-[11.5px] me-text-mute italic">
        DAG editor (add/remove steps + edges) hidden. Click <strong>destructive: locked</strong>
        in the page header to arm destructive controls.
      </div>
    )
  }
  return <DagEditor {...props} />
}


function DagEditor({
  step,
  steps,
  onChange,
}: {
  step: ComputationStep
  steps: ComputationStep[]
  onChange: () => void
}) {
  const qc = useQueryClient()
  const slots = useQuery({ queryKey: ['promptSlots'], queryFn: () => slotsApi.list() })
  const allDeps = useQuery({ queryKey: ['stepDeps'], queryFn: () => stepDepsApi.list() })

  const byId = new Map(steps.map((s) => [s.id, s]))

  // Map (parent, child) → dep id, so we know what to DELETE.
  const depIdByEdge = useMemo(() => {
    const m = new Map<string, number>()
    for (const d of allDeps.data ?? []) m.set(`${d.parent}->${d.child}`, d.id)
    return m
  }, [allDeps.data])

  const addEdge = useMutation({
    mutationFn: ({ parent, child }: { parent: number; child: number }) =>
      stepDepsApi.create(parent, child),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      qc.invalidateQueries({ queryKey: ['steps'] })
      onChange()
    },
  })
  const removeEdge = useMutation({
    mutationFn: (depId: number) => stepDepsApi.remove(depId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      qc.invalidateQueries({ queryKey: ['steps'] })
      onChange()
    },
  })
  const removeStep = useMutation({
    mutationFn: () => stepsApi.remove(step.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['steps'] })
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      onChange()
    },
  })

  const [showAddChild, setShowAddChild] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  function dropEdge(parentId: number, childId: number) {
    const id = depIdByEdge.get(`${parentId}->${childId}`)
    if (id != null) removeEdge.mutate(id)
  }

  // Eligible parents to add: any step that isn't already a parent + isn't this step + isn't a descendant.
  const descendants = collectDescendants(step.id, steps)
  const eligibleParents = steps.filter(
    (s) => s.id !== step.id && !step.parent_ids.includes(s.id) && !descendants.has(s.id),
  )
  const eligibleChildren = steps.filter(
    (s) => s.id !== step.id && !step.child_ids.includes(s.id) && !collectAncestors(step.id, steps).has(s.id),
  )

  return (
    <div className="mt-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <EdgeColumn
          label="Depends on (parents)"
          empty="No parents — root step."
          ids={step.parent_ids}
          byId={byId}
          onRemove={(pid) => dropEdge(pid, step.id)}
        />
        <EdgeColumn
          label="Feeds into (children)"
          empty="No children — leaf step."
          ids={step.child_ids}
          byId={byId}
          onRemove={(cid) => dropEdge(step.id, cid)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <AddEdgePicker
          label="Add parent"
          steps={eligibleParents}
          onPick={(pid) => addEdge.mutate({ parent: pid, child: step.id })}
          pending={addEdge.isPending}
          error={addEdge.error?.message}
        />
        <AddEdgePicker
          label="Add child"
          steps={eligibleChildren}
          onPick={(cid) => addEdge.mutate({ parent: step.id, child: cid })}
          pending={addEdge.isPending}
          error={addEdge.error?.message}
        />
      </div>

      <div className="me-card p-3">
        <div className="flex items-center justify-between">
          <div className="me-section-h !mb-0">Step actions</div>
          <button
            onClick={() => setShowAddChild((v) => !v)}
            className="me-btn me-btn-outline"
          >
            <Plus size={12} />
            New step from {step.name}
          </button>
        </div>
        {showAddChild && (
          <NewStepForm
            parent={step}
            slots={slots.data ?? []}
            onClose={() => setShowAddChild(false)}
            onCreated={() => {
              setShowAddChild(false)
              onChange()
            }}
          />
        )}
        <div className="mt-2 flex items-center gap-2">
          {!confirmingDelete ? (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="me-btn me-btn-outline text-[color:var(--me-stale)]"
              title={`Delete step ${step.name}`}
            >
              <Trash2 size={12} />
              Delete this step
            </button>
          ) : (
            <>
              <span className="text-[12px] text-[color:var(--me-stale)]">
                Confirm delete? This removes the step + every edge touching it. Existing ComputationResults are kept (orphaned step ref is allowed).
              </span>
              <button
                onClick={() => removeStep.mutate()}
                disabled={removeStep.isPending}
                className="me-btn me-btn-primary"
              >
                Confirm
              </button>
              <button
                onClick={() => setConfirmingDelete(false)}
                className="me-btn me-btn-outline"
              >
                Cancel
              </button>
            </>
          )}
        </div>
        {removeStep.error && (
          <div className="mt-2 text-[11px] text-[color:var(--me-stale)]">
            {(removeStep.error as Error).message}
          </div>
        )}
      </div>
    </div>
  )
}

function EdgeColumn({
  label, empty, ids, byId, onRemove,
}: {
  label: string
  empty: string
  ids: number[]
  byId: Map<number, ComputationStep>
  onRemove: (otherId: number) => void
}) {
  return (
    <div className="me-card p-2.5">
      <div className="me-section-h !mb-1">{label}</div>
      {ids.length === 0 ? (
        <div className="text-[11px] me-text-mute italic">{empty}</div>
      ) : (
        <ul className="space-y-1">
          {ids.map((id) => (
            <li key={id} className="flex items-center justify-between gap-2 rounded-sm bg-[color:var(--me-surfaceHover)] px-2 py-1">
              <span className="me-mono text-[11.5px] truncate">{byId.get(id)?.name ?? id}</span>
              <button
                onClick={() => onRemove(id)}
                className="me-btn me-btn-outline shrink-0"
                title="Remove edge"
              >
                <X size={10} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function AddEdgePicker({
  label, steps, onPick, pending, error,
}: {
  label: string
  steps: ComputationStep[]
  onPick: (id: number) => void
  pending: boolean
  error?: string
}) {
  const [val, setVal] = useState<number | ''>('')
  return (
    <div className="me-card p-2.5">
      <div className="me-section-h !mb-1">{label}</div>
      <div className="flex items-center gap-1">
        <select
          value={val}
          onChange={(e) => setVal(e.target.value ? Number(e.target.value) : '')}
          className="me-input flex-1"
          disabled={steps.length === 0 || pending}
        >
          <option value="">{steps.length === 0 ? 'no eligible steps' : 'choose…'}</option>
          {steps.map((s) => (
            <option key={s.id} value={s.id}>L{s.level} {s.name}</option>
          ))}
        </select>
        <button
          onClick={() => val !== '' && onPick(Number(val))}
          disabled={val === '' || pending}
          className="me-btn me-btn-primary"
          title="Add edge"
        >
          <Plus size={12} />
        </button>
      </div>
      {error && (
        <div className="mt-1 text-[11px] text-[color:var(--me-stale)]">{error}</div>
      )}
    </div>
  )
}

function NewStepForm({
  parent, slots, onClose, onCreated,
}: {
  parent: ComputationStep
  slots: PromptSlot[]
  onClose: () => void
  onCreated: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [slotId, setSlotId] = useState<number | ''>('')
  const [fanoutKind, setFanoutKind] = useState<'none' | 'per_matter' | 'matter_only'>('none')

  const create = useMutation({
    mutationFn: async () => {
      if (slotId === '') throw new Error('pick a slot')
      // Create the step
      const step = await stepsApi.create({
        name: name.trim(),
        slot: slotId,
        prompt_version_label: 'production',
        backend: 'runpod',
        fanout_kind: fanoutKind,
      })
      // Wire it as a child of `parent`
      await stepDepsApi.create(parent.id, step.id)
      return step
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['steps'] })
      qc.invalidateQueries({ queryKey: ['stepDeps'] })
      onCreated()
    },
  })

  // Slots that aren't already used by another step (each slot → 0/1 step in practice).
  const slotsAvailable = slots

  return (
    <div className="mt-3 rounded-sm border me-border p-3">
      <div className="me-section-h !mb-2">
        New step downstream of <span className="me-mono">{parent.name}</span>
      </div>
      <div className="space-y-2">
        <div>
          <label className="text-[11px] me-text-mute">Name (unique)</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. detect_priority_signal"
            className="me-input mt-0.5 w-full"
          />
        </div>
        <div>
          <label className="text-[11px] me-text-mute">Slot (existing — create new slot from /prompts if needed)</label>
          <select
            value={slotId}
            onChange={(e) => setSlotId(e.target.value ? Number(e.target.value) : '')}
            className="me-input mt-0.5 w-full"
          >
            <option value="">choose…</option>
            {slotsAvailable.map((s) => (
              <option key={s.id} value={s.id}>L{s.level} {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[11px] me-text-mute">Fan-out</label>
          <select
            value={fanoutKind}
            onChange={(e) => setFanoutKind(e.target.value as typeof fanoutKind)}
            className="me-input mt-0.5 w-full"
          >
            <option value="none">none — one result per artifact</option>
            <option value="per_matter">per_matter — runs once per linked matter</option>
            <option value="matter_only">matter_only — matter-level only</option>
          </select>
        </div>
        <div className="flex justify-end gap-1">
          <button onClick={onClose} className="me-btn me-btn-outline">
            Cancel
          </button>
          <button
            onClick={() => create.mutate()}
            disabled={!name.trim() || slotId === '' || create.isPending}
            className="me-btn me-btn-primary"
          >
            {create.isPending ? 'Creating…' : 'Create + wire'}
          </button>
        </div>
        {create.error && (
          <div className="text-[11px] text-[color:var(--me-stale)]">
            {(create.error as Error).message}
          </div>
        )}
      </div>
    </div>
  )
}
